import React from 'react';
import store2 from 'store2';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { AssetTypes } from '../../utils/enums/AssetTypes';
import {
    EditPanel,
    EditPanelCell,
    EditPanelFilterMultiSelect,
    EditPanelInput,
    EditPanelRow,
    ISelectOption,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelSpacing,
    PanelWithoutForm,
} from '../elements';
import { Spinner } from '../spinners';
import { IAsset } from '../Surveys/Interfaces/IAsset';
import { DocumentsGrid } from '../utils/DocumentsGrid';
import { DocumentsList } from '../utils/DocumentsList';
import { transformLabelsToSelectOptions } from '../utils/entityLabels.utils';
import { useReactAsync } from '../utils/useReactAsync';

interface IDetailsDocumentsProps {
    objectId: string;
    surveyId?: string;
    isIncludeImages?: boolean;
    context: 'object' | 'survey';
}

async function loadDocuments({
    objectId,
    surveyId,
    labels,
    searchText,
    isIncludeImages,
}: {
    objectId?: string | undefined;
    surveyId?: string | undefined;
    labels: string[];
    searchText: string;
    isIncludeImages: boolean;
}): Promise<IAsset[]> {
    let url = '';
    let queryString = {};

    if (surveyId) {
        url = '/api/assets';
        let types = `${AssetTypes.Document},${AssetTypes['3DModel']},${AssetTypes.MeasurementXLS},${AssetTypes.MeasurementPDF},${AssetTypes.Text},${AssetTypes.BIM},${AssetTypes.ZipArchive},${AssetTypes.TarArchive},${AssetTypes.AutoCad},${AssetTypes.ImageTif},${AssetTypes.IfcModel}`;
        if (isIncludeImages) {
            types += `,${AssetTypes.Image},${AssetTypes.Image360}`;
        }
        queryString = {
            surveyId,
            types,
            labels,
            searchText,
            unassigned: true,
        };
    } else {
        url = `/api/objects/${objectId}/assets`;
        queryString = {
            types: `${AssetTypes.Document},${AssetTypes.Image},${AssetTypes.Image360},${AssetTypes.MeasurementXLS},${AssetTypes.MeasurementPDF},${AssetTypes.Text},${AssetTypes.BIM},${AssetTypes.ZipArchive},${AssetTypes.TarArchive},${AssetTypes.AutoCad},${AssetTypes.ImageTif},${AssetTypes.ImageAnnotation},${AssetTypes['3DModel']}`,
            unassigned: true,
            labels,
            searchText,
        };
    }

    const result = await fetchGet(url, queryString, ['404']);
    return result.json as IAsset[];
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const DetailsDocuments: React.FC<IDetailsDocumentsProps> = ({ objectId, surveyId, isIncludeImages = false }) => {
    const translate = useTranslate();
    const [selectedLabels, setSelectedLabels] = React.useState<string[]>([]);
    const [documentSearchText, setDocumentSearchText] = React.useState<string>('');
    const [documentSearchString, setDocumentSearchString] = React.useState<string>('');
    const [knownLabels, setKnownLabels] = React.useState<ISelectOption[]>([]);
    const [isListView, setIsListView] = React.useState({ visible: false, updated: false });
    const [isLabelFilteringShown, setIsLabelFilteringShown] = React.useState<boolean>(false);

    const { data, isLoading } = useReactAsync(
        loadDocuments,
        { objectId, surveyId, labels: selectedLabels, searchText: documentSearchString, isIncludeImages },
        [selectedLabels, documentSearchString, isIncludeImages],
    );

    React.useEffect(() => {
        if (store2.get('DetailsDocumentsListVisible')) {
            setIsListView({ visible: store2.get('DetailsDocumentsListVisible') as boolean, updated: false });
        }
    }, []);

    React.useEffect(() => {
        if (isListView.updated) {
            store2.set('DetailsDocumentsListVisible', isListView.visible);
        }
    }, [isListView]);

    React.useEffect(() => {
        const loadAndSaveLabels = async () => {
            const url = `/api/objects/${objectId}/assets/knownLabels`;
            const result = await fetchGet(url);
            const labels = result.json as string[];
            const options = transformLabelsToSelectOptions(labels);
            setKnownLabels(options);
        };

        void loadAndSaveLabels();
    }, [objectId]);

    const toggleIsLabelFilteringShown = () => {
        setIsLabelFilteringShown((value) => !value);
    };

    const handleLabelOptionSelected = (labels: string[]): void => {
        setSelectedLabels(labels);
    };

    const handleDescriptionEnter = () => {
        setDocumentSearchString(documentSearchText);
    };

    return (
        <PanelWithoutForm id="Documents">
            <PanelHeading text={`${translate('survey.documents')}`} icon="fa fa-book">
                {isListView.visible ? (
                    <>
                        <PanelButtonLink onClick={toggleIsLabelFilteringShown} text="filter" icon="fa fa-search" small={true} />
                        <PanelButtonLink
                            onClick={() => setIsListView({ visible: false, updated: true })}
                            text={translate('equipment.gridView')}
                            icon="fa fa-th"
                            small={true}
                        />
                    </>
                ) : (
                    <PanelButtonLink
                        onClick={() => setIsListView({ visible: true, updated: true })}
                        text={translate('equipment.listView')}
                        icon="fa fa-list"
                        small={true}
                    />
                )}
            </PanelHeading>
            <PanelBody noPadding={true} whiteBackground={true} isForm={true}>
                {(isLoading && <Spinner />) || (
                    <>
                        {data && !isListView.visible && (
                            <PanelSpacing>
                                <DocumentsGrid assets={data} objectId={objectId} surveyId={surveyId} />
                            </PanelSpacing>
                        )}
                        {data && isListView.visible && (
                            <>
                                {isLabelFilteringShown && (
                                    <EditPanel bottomMargin={false} noPadding={false}>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelFilterMultiSelect
                                                    topLabel={translate('asset.filterOnLabel')}
                                                    name="labels"
                                                    placeholder={translate('common.selectItem', translate('common.labels'))}
                                                    value={selectedLabels}
                                                    options={knownLabels}
                                                    handleChange={handleLabelOptionSelected}
                                                />
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelInput
                                                    topLabel={translate('asset.filterOnText')}
                                                    name="documentSearchText"
                                                    value={documentSearchText}
                                                    handleChange={setDocumentSearchText}
                                                    handleEnter={handleDescriptionEnter}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    </EditPanel>
                                )}

                                <DocumentsList assets={data} objectId={objectId} surveyId={surveyId} />
                            </>
                        )}
                    </>
                )}
            </PanelBody>
        </PanelWithoutForm>
    );
};
